import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { publicErrorMessage } from '../../public/auth-errors.js';
import { PUBLIC_FORM } from '../../public/classes.js';
import { PasswordInput } from '../../public/PasswordInput.js';
import { PublicShell } from '../../public/PublicShell.js';
import { api, ApiError } from '../../shared/api.js';
import { Button, buttonClasses } from '../../ui/Button.js';
import { Field, Input } from '../../ui/Field.js';
import { FormActions } from '../../ui/FormActions.js';
import { Alert, SkeletonList } from '../../ui/States.js';
import { ROLE_LABELS } from '../lib/format.js';
import { clearHash, readHashToken } from '../lib/hash.js';
import { useEnterSession } from '../lib/session.js';

const MIN_LENGTH = 10;

/**
 * Invitation dans l'équipe d'un garage (jeton dans le fragment `#`). Nouveau compte : nom et mot
 * de passe ; compte existant : confirmation par le mot de passe actuel.
 */
export default function AcceptInvitationPage() {
  const [token] = useState(readHashToken);
  const enter = useEnterSession();
  const [typedName, setName] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; password?: string }>({});
  const inspect = useQuery({
    queryKey: ['invitations.inspect', token],
    queryFn: () => api('invitations.inspect', { body: { token } }),
    enabled: token.length > 0,
    retry: false,
  });
  // Vague 5 : nom saisi par la plateforme à la création du garage, prérempli et modifiable.
  const name = typedName ?? inspect.data?.inviteeName ?? '';
  const accept = useMutation({
    mutationFn: (accountExists: boolean) =>
      api('invitations.accept', { body: { token, password, ...(accountExists ? {} : { name: name.trim() }) } }),
    onSuccess: async (me) => {
      clearHash();
      await enter(me);
    },
  });

  if (!token || inspect.isError) {
    return (
      <PublicShell title="Invitation expirée">
        <Alert tone="warning" title="Cette invitation n’est plus valable.">
          Elle a expiré, a déjà servi ou a été retirée. Demandez au garage de vous en envoyer une nouvelle.
        </Alert>
        <Link to="/login" className={buttonClasses({ variant: 'secondary', block: true, className: 'mt-6' })}>
          Aller à la connexion
        </Link>
      </PublicShell>
    );
  }
  if (inspect.isPending) {
    return (
      <PublicShell title="Invitation">
        <SkeletonList rows={2} />
      </PublicShell>
    );
  }

  const inv = inspect.data;
  // 401 de l'API : mot de passe du compte existant incorrect (jamais « session expirée » ici).
  const error =
    accept.error instanceof ApiError && accept.error.status === 401
      ? inv.accountExists
        ? 'Mot de passe incorrect pour ce compte.'
        : 'Indiquez votre nom pour créer le compte.'
      : publicErrorMessage(accept.error);
  return (
    <PublicShell
      title={`Rejoindre ${inv.garageName}`}
      intro={
        <>
          Vous êtes invité comme <strong className="font-semibold text-ink">{ROLE_LABELS[inv.role]}</strong> avec
          l’adresse <span className="font-medium break-words text-ink">{inv.email}</span>.
        </>
      }
    >
      <form
        className={PUBLIC_FORM}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          const next: typeof fieldErrors = {};
          if (!inv.accountExists && !name.trim()) next.name = 'Indiquez votre nom.';
          if (inv.accountExists && !password) next.password = 'Indiquez le mot de passe de votre compte.';
          if (!inv.accountExists && password.length < MIN_LENGTH)
            next.password = `Au moins ${MIN_LENGTH} caractères (${password.length} saisis).`;
          setFieldErrors(next);
          if (next.name || next.password) return;
          accept.mutate(inv.accountExists);
        }}
      >
        {/* Identifiant pour le gestionnaire de mots de passe (e-mail de l'invitation, non modifiable). */}
        <input type="email" name="email" autoComplete="username" value={inv.email} readOnly hidden />
        {inv.accountExists ? (
          <Field label="Votre mot de passe actuel" hint="Un compte existe déjà pour cette adresse : confirmez avec son mot de passe."
            error={fieldErrors.password}
          >
            <PasswordInput
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
        ) : (
          <>
            <Field label="Votre nom" error={fieldErrors.name}>
              <Input name="name" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Choisissez un mot de passe" hint={`Au moins ${MIN_LENGTH} caractères.`} error={fieldErrors.password}>
              <PasswordInput
                name="new-password"
                autoComplete="new-password"
                required
                minLength={MIN_LENGTH}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
          </>
        )}
        {error ? (
          <Alert tone="danger" role="alert">
            {error}
          </Alert>
        ) : null}
        <FormActions>
          <Button type="submit" size="lg" block loading={accept.isPending}>
            Rejoindre l’équipe
          </Button>
        </FormActions>
      </form>
    </PublicShell>
  );
}
